import { useTournament } from '../context/TournamentContext';
import { MatchCard } from '../components/MatchCard';
import { BracketDiagram } from '../components/BracketDiagram';
import { Clock, MapPin, Swords } from 'lucide-react';
import type { MatchUp } from '../data/types';
import { PartnersCard } from '../components/PartnersCard';

function MatchGroup({ matchUp }: { matchUp: MatchUp }) {
  const { getTeam, getGamesByMatchUp } = useTournament();
  const teamA = matchUp.team_a_id ? getTeam(matchUp.team_a_id) : null;
  const teamB = matchUp.team_b_id ? getTeam(matchUp.team_b_id) : null;
  const games = getGamesByMatchUp(matchUp.id);
  // First game's tee_time (Single 1 is typically game_number 1)
  const firstGame = [...games].sort((a, b) => a.game_number - b.game_number)[0];
  const displayTime = firstGame?.tee_time ?? matchUp.scheduled_time;

  return (
    <div>
      {/* Match-up header */}
      <div className="matchup-group-header">
        <div className="matchup-group-identity">
          <Swords size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
          <span className="matchup-group-label">{matchUp.label}</span>
          <span className="matchup-group-teams">
            <span style={{ color: teamA?.color, fontWeight: 700 }}>
              {teamA?.short_name ?? teamA?.name ?? 'TBD'}
            </span>
            <span style={{ color: 'var(--text-muted)', margin: '0 0.3rem' }}>vs</span>
            <span style={{ color: teamB?.color, fontWeight: 700 }}>
              {teamB?.short_name ?? teamB?.name ?? 'TBD'}
            </span>
          </span>
        </div>
        {displayTime && (
          <div className="matchup-group-time">
            <Clock size={12} style={{ color: 'var(--text-muted)' }} />
            <span>{displayTime}</span>
          </div>
        )}
      </div>

      <MatchCard matchUp={matchUp} />
    </div>
  );
}

export function HomePage() {
  const { matchUps, isLoading } = useTournament();

  if (isLoading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  const morningMatches = matchUps.filter(m => m.round.startsWith('semi'));
  const afternoonMatches = matchUps.filter(m => !m.round.startsWith('semi'));

  return (
    <div>
      {/* Hero */}
      <div className="hero">
        <h1 className="hero-title">FINÁLE</h1>
        <div className="hero-date">23. DUBNA 2026</div>
        <div className="hero-venue">
          <MapPin size={14} style={{ verticalAlign: 'middle', marginRight: '0.25rem' }} />
          Golf Vinoř — Prague City Golf
        </div>
      </div>

      {/* Morning - Semifinals */}
      <div className="mb-lg" style={{ marginTop: '2.5rem' }}>
        <div className="section-header">
          <h2>Dopoledne — Semifinále</h2>
          {morningMatches[0]?.scheduled_time && (
            <span className="badge badge-scheduled" style={{ fontSize: '0.6875rem' }}>
              {morningMatches[0].scheduled_time}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-lg">
          {morningMatches.map(m => (
            <MatchGroup key={m.id} matchUp={m} />
          ))}
          {morningMatches.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">⛳</div>
              <p>Semifinálové zápasy zatím nebyly vytvořeny</p>
            </div>
          )}
        </div>
      </div>

      {/* Afternoon - Placement */}
      <div className="mb-lg">
        <div className="section-header">
          <h2>Odpoledne — O umístění</h2>
          {afternoonMatches[0]?.scheduled_time && (
            <span className="badge badge-scheduled" style={{ fontSize: '0.6875rem' }}>
              {afternoonMatches[0].scheduled_time}
            </span>
          )}
        </div>
        <div className="flex flex-col gap-lg">
          {afternoonMatches.map(m => (
            <MatchGroup key={m.id} matchUp={m} />
          ))}
          {afternoonMatches.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">🏆</div>
              <p>Odpolední zápasy budou doplněny po semifinále</p>
            </div>
          )}
        </div>
      </div>

      {/* Bracket Diagram */}
      <div className="mb-lg">
        <BracketDiagram />
      </div>

      {/* Partners */}
      <PartnersCard />
    </div>
  );
}
